import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  ScrollText, RefreshCw, Plus, X, ExternalLink, Trash2, Edit2, Filter,
  ChevronRight, Search, Boxes, Siren, ShieldAlert, FileText, CheckCircle2,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

type Status = 'proposed' | 'accepted' | 'deprecated' | 'superseded'

interface ServiceRef { id: number; name: string; tier: number }
interface IncidentRef { id: number; title: string; severity: string; status: string }

interface Decision {
  id: number
  title: string
  status: Status
  context: string
  decision: string
  consequences: string
  alternatives: string
  deciders: string
  decided_at: string | null
  related_services: number[]
  related_services_expanded?: ServiceRef[]
  related_risks: string[]
  related_incidents: number[]
  related_incidents_expanded?: IncidentRef[]
  supersedes_id: number | null
  tags: string[]
  url: string | null
  created_at: string | null
  updated_at: string | null
}

interface Stats {
  total: number
  by_status: Record<string, number>
  last_90d: number
  latest: { id: number; title: string; status: string; created_at: string | null }[]
}

const ALL_STATUSES: Status[] = ['proposed', 'accepted', 'deprecated', 'superseded']

const STATUS_BG: Record<Status, string> = {
  proposed:   'bg-blue/10 border-blue/30 text-blue-light',
  accepted:   'bg-emerald/10 border-emerald/30 text-emerald',
  deprecated: 'bg-amber/10 border-amber/30 text-amber-light',
  superseded: 'bg-text-muted/10 border-border text-text-muted',
}

function formatDate(s: string | null): string {
  if (!s) return '—'
  return s.slice(0, 10)
}

function localDateForInput(s: string | null): string {
  if (!s) return ''
  return s.slice(0, 10)
}

// ── Form ─────────────────────────────────────────────────────────────────────

function DecisionForm({
  initial,
  serviceOptions,
  incidentOptions,
  onSave,
  onCancel,
}: {
  initial?: Partial<Decision>
  serviceOptions: { id: number; name: string; tier: number }[]
  incidentOptions: { id: number; title: string; severity: string }[]
  onSave: (d: any) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initial?.title || '')
  const [status, setStatus] = useState<Status>(initial?.status || 'proposed')
  const [context, setContext] = useState(initial?.context || '')
  const [decisionText, setDecisionText] = useState(initial?.decision || '')
  const [consequences, setConsequences] = useState(initial?.consequences || '')
  const [alternatives, setAlternatives] = useState(initial?.alternatives || '')
  const [deciders, setDeciders] = useState(initial?.deciders || '')
  const [decidedAt, setDecidedAt] = useState(localDateForInput(initial?.decided_at || null))
  const [services, setServices] = useState<number[]>(initial?.related_services || [])
  const [incidents, setIncidents] = useState<number[]>(initial?.related_incidents || [])
  const [tags, setTags] = useState((initial?.tags || []).join(', '))
  const [url, setUrl] = useState(initial?.url || '')

  const toggle = (id: number, list: number[], setter: (v: number[]) => void) => {
    setter(list.includes(id) ? list.filter(x => x !== id) : [...list, id])
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Title *</label>
        <input value={title} onChange={e => setTitle(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="e.g. Adopt Postgres for primary datastore" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Status</label>
          <div className="flex gap-1">
            {ALL_STATUSES.map(s => (
              <button type="button" key={s} onClick={() => setStatus(s)}
                className={clsx('flex-1 py-1 text-[10px] rounded border capitalize transition-colors',
                  status === s ? STATUS_BG[s] : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Decided on</label>
          <input type="date" value={decidedAt} onChange={e => setDecidedAt(e.target.value)} title="Decided on" className="input py-1.5 text-[12px] w-full" />
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Context</label>
        <textarea value={context} onChange={e => setContext(e.target.value)} rows={3} className="input py-1.5 text-[12px] w-full" placeholder="What's the situation? What problem are we solving? What are the constraints?" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Decision</label>
        <textarea value={decisionText} onChange={e => setDecisionText(e.target.value)} rows={3} className="input py-1.5 text-[12px] w-full" placeholder="What did we decide? Be specific and unambiguous." />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Consequences</label>
        <textarea value={consequences} onChange={e => setConsequences(e.target.value)} rows={3} className="input py-1.5 text-[12px] w-full" placeholder="Trade-offs, follow-on work, risks accepted, what we give up." />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Alternatives considered</label>
        <textarea value={alternatives} onChange={e => setAlternatives(e.target.value)} rows={3} className="input py-1.5 text-[12px] w-full" placeholder="What did we look at and reject? Why?" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Deciders</label>
          <input value={deciders} onChange={e => setDeciders(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="who signed off" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">External URL</label>
          <input value={url || ''} onChange={e => setUrl(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="Notion / Confluence link" />
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Affects services</label>
        <div className="flex flex-wrap gap-1">
          {serviceOptions.length === 0 && <span className="text-[10px] text-text-muted italic">No services in catalog.</span>}
          {serviceOptions.map(s => (
            <button type="button" key={s.id} onClick={() => toggle(s.id, services, setServices)}
              className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
                services.includes(s.id)
                  ? 'bg-blue/10 border-blue/30 text-blue-light'
                  : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}>
              T{s.tier} {s.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Linked incidents</label>
        <div className="flex flex-wrap gap-1">
          {incidentOptions.length === 0 && <span className="text-[10px] text-text-muted italic">No incidents in postmortem log.</span>}
          {incidentOptions.map(i => (
            <button type="button" key={i.id} onClick={() => toggle(i.id, incidents, setIncidents)}
              className={clsx('px-2 py-1 text-[10px] rounded border transition-colors max-w-xs truncate',
                incidents.includes(i.id)
                  ? 'bg-rose/10 border-rose/30 text-rose'
                  : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}
              title={i.title}>
              {i.severity.toUpperCase()} {i.title}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Tags</label>
        <input value={tags} onChange={e => setTags(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="ai, infra, vendor" />
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button type="button"
          disabled={!title.trim()}
          onClick={() => onSave({
            title, status, context, decision: decisionText, consequences, alternatives,
            deciders, decided_at: decidedAt ? new Date(decidedAt).toISOString() : null,
            related_services: services, related_incidents: incidents, related_risks: [],
            tags: tags.split(',').map(t => t.trim()).filter(Boolean),
            url: url || null,
            supersedes_id: null,
          })}
          className="btn-primary text-xs">
          Save decision
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
      </div>
    </div>
  )
}

// ── Detail drawer ────────────────────────────────────────────────────────────

function DecisionDetail({
  decision, onClose, onEdit, onDelete,
}: {
  decision: Decision
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const navigate = useNavigate()
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-surface-card border-l border-border shadow-2xl z-40 overflow-y-auto">
      <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={clsx('px-1.5 py-0.5 text-[10px] font-semibold rounded border capitalize', STATUS_BG[decision.status])}>
            {decision.status}
          </span>
          <span className="text-[10px] text-text-muted">ADR-{decision.id}</span>
        </div>
        <button type="button" onClick={onClose} title="Close" className="text-text-muted hover:text-text-primary">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h2 className="text-base font-bold text-text-primary leading-snug">{decision.title}</h2>
          <div className="text-[10px] text-text-muted mt-1">
            {decision.deciders && <>by {decision.deciders} · </>}
            {decision.decided_at ? `decided ${formatDate(decision.decided_at)}` : `created ${formatDate(decision.created_at)}`}
          </div>
        </div>

        {decision.context && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Context</div>
            <p className="text-[11px] text-text-secondary whitespace-pre-wrap leading-relaxed">{decision.context}</p>
          </div>
        )}
        {decision.decision && (
          <div className="border-l-2 border-blue/30 pl-3">
            <div className="text-[9px] uppercase tracking-wider text-blue-light mb-1">Decision</div>
            <p className="text-[11px] text-text-primary whitespace-pre-wrap leading-relaxed">{decision.decision}</p>
          </div>
        )}
        {decision.consequences && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Consequences</div>
            <p className="text-[11px] text-text-secondary whitespace-pre-wrap leading-relaxed">{decision.consequences}</p>
          </div>
        )}
        {decision.alternatives && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Alternatives considered</div>
            <p className="text-[11px] text-text-secondary whitespace-pre-wrap leading-relaxed">{decision.alternatives}</p>
          </div>
        )}

        {decision.related_services_expanded && decision.related_services_expanded.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
              <Boxes size={10} /> Affects services
            </div>
            <div className="flex flex-wrap gap-1">
              {decision.related_services_expanded.map(s => (
                <button type="button" key={s.id}
                  onClick={() => navigate('/app/services')}
                  className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border hover:brightness-125',
                    s.tier === 1 ? 'bg-rose/10 border-rose/30 text-rose'
                      : s.tier === 2 ? 'bg-amber/10 border-amber/30 text-amber-light'
                      : 'bg-emerald/10 border-emerald/30 text-emerald'
                  )}>
                  <span className="font-mono font-semibold">T{s.tier}</span>
                  <span>{s.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {decision.related_incidents_expanded && decision.related_incidents_expanded.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
              <Siren size={10} /> Linked incidents
            </div>
            <div className="space-y-1">
              {decision.related_incidents_expanded.map(i => (
                <button type="button" key={i.id}
                  onClick={() => navigate('/app/postmortems')}
                  className="block w-full text-left px-2 py-1 rounded border border-border bg-surface-raised hover:bg-surface-hover text-[11px]">
                  <span className="text-text-muted mr-1">{i.severity.toUpperCase()}</span>
                  <span className="text-text-primary">{i.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {decision.tags.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Tags</div>
            <div className="flex flex-wrap gap-1">
              {decision.tags.map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-surface-raised text-text-secondary">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {decision.url && (
          <a href={decision.url} target="_blank" rel="noreferrer noopener"
             className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1">
            <FileText size={11} /> External document <ExternalLink size={10} />
          </a>
        )}

        <div className="pt-3 border-t border-border flex gap-2">
          <button type="button" onClick={onEdit} className="btn-secondary text-xs">
            <Edit2 size={11} /> Edit
          </button>
          <button type="button"
            onClick={() => { if (confirm(`Delete ADR "${decision.title}"?`)) onDelete() }}
            className="btn-ghost text-xs text-rose">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function DecisionsPage() {
  const [decisions, setDecisions] = useState<Decision[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [services, setServices] = useState<{ id: number; name: string; tier: number }[]>([])
  const [incidents, setIncidents] = useState<{ id: number; title: string; severity: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Decision | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Decision | null>(null)
  const [searchQ, setSearchQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<Status | ''>('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: list }, { data: s }, { data: svc }, { data: pm }] = await Promise.all([
        api.get('/api/decisions'),
        api.get('/api/decisions/stats'),
        api.get('/api/services'),
        api.get('/api/postmortems'),
      ])
      setDecisions(list.decisions)
      setStats(s)
      setServices((svc.services || []).map((x: any) => ({ id: x.id, name: x.name, tier: x.tier })))
      setIncidents((pm.incidents || []).map((x: any) => ({ id: x.id, title: x.title, severity: x.severity })))
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const seedIfEmpty = async () => {
    if (decisions.length === 0) {
      await api.post('/api/decisions/seed')
      fetchData()
    }
  }

  const save = async (d: any) => {
    if (editing) {
      await api.put(`/api/decisions/${editing.id}`, d)
    } else {
      await api.post('/api/decisions', d)
    }
    setShowForm(false); setEditing(null); setSelected(null)
    fetchData()
  }

  const remove = async (id: number) => {
    await api.delete(`/api/decisions/${id}`)
    setSelected(null)
    fetchData()
  }

  const filtered = useMemo(() => {
    return decisions.filter(d => {
      if (statusFilter && d.status !== statusFilter) return false
      if (searchQ) {
        const q = searchQ.toLowerCase()
        const hay = `${d.title} ${d.context} ${d.decision} ${d.consequences} ${d.tags.join(' ')}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [decisions, statusFilter, searchQ])

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }
  const isEmpty = decisions.length === 0

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <ScrollText size={20} className="text-amber" /> Decision Log (ADRs)
          </h1>
          <p className="text-[10px] text-text-secondary">
            Architectural Decision Records — context, decision, consequences, alternatives. Linked to services and incidents.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEmpty && (
            <button type="button" onClick={seedIfEmpty} className="btn-secondary text-xs" title="Insert example ADRs">
              <Plus size={12} /> Seed examples
            </button>
          )}
          <button type="button" onClick={fetchData} className="btn-ghost text-xs" title="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => { setEditing(null); setShowForm(true) }} className="btn-primary text-xs">
            <Plus size={12} /> New ADR
          </button>
        </div>
      </div>

      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Total</div>
            <div className="text-lg font-bold text-text-primary">{stats.total}</div>
            <div className="text-[10px] text-text-muted">{stats.last_90d} in last 90d</div>
          </div>
          {ALL_STATUSES.map(s => (
            <div key={s} className={clsx('card p-3 border', STATUS_BG[s])}>
              <div className={clsx('text-[10px] uppercase tracking-wider font-semibold capitalize')}>{s}</div>
              <div className="text-lg font-bold">{stats.by_status[s] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {!isEmpty && (
        <div className="card p-3 flex flex-wrap items-center gap-3">
          <Filter size={12} className="text-text-muted" />
          <button type="button" onClick={() => setStatusFilter('')}
            className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
              !statusFilter ? 'bg-blue/10 border-blue/30 text-blue-light' : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
            )}>All</button>
          {ALL_STATUSES.map(s => (
            <button type="button" key={s} onClick={() => setStatusFilter(s)}
              className={clsx('px-2 py-1 text-[10px] rounded border capitalize transition-colors',
                statusFilter === s ? STATUS_BG[s] : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}>{s}</button>
          ))}
          <div className="flex items-center gap-1 ml-auto">
            <Search size={12} className="text-text-muted" />
            <input type="search" value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search title / context / tag…" className="input py-1 text-[11px] w-48" />
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="card p-10 text-center">
          <ScrollText size={36} className="text-text-muted mx-auto mb-3" />
          <div className="text-sm font-semibold text-text-primary mb-1">No decisions logged yet</div>
          <p className="text-[11px] text-text-secondary mb-4 max-w-md mx-auto">
            ADRs capture the why behind technical choices — context, decision, consequences, alternatives.
            Linked to services and incidents for full traceability.
          </p>
          <button type="button" onClick={seedIfEmpty} className="btn-primary text-xs">
            <Plus size={12} /> Seed with two example ADRs
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => (
            <button type="button" key={d.id} onClick={() => setSelected(d)}
              className="card p-3 w-full text-left hover:bg-surface-raised/50 transition-colors block">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx('px-1.5 py-0.5 text-[9px] font-semibold rounded border capitalize', STATUS_BG[d.status])}>
                      {d.status}
                    </span>
                    <span className="text-[9px] text-text-muted font-mono">ADR-{d.id}</span>
                    {d.decided_at && (
                      <span className="text-[9px] text-text-muted">decided {formatDate(d.decided_at)}</span>
                    )}
                  </div>
                  <div className="text-[12px] font-semibold text-text-primary leading-snug">{d.title}</div>
                  {d.context && (
                    <p className="text-[10px] text-text-muted truncate mt-0.5">{d.context.split('\n')[0]}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {d.related_services_expanded?.slice(0, 4).map(s => (
                      <span key={s.id}
                        className={clsx('inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] border',
                          s.tier === 1 ? 'bg-rose/10 border-rose/30 text-rose'
                            : s.tier === 2 ? 'bg-amber/10 border-amber/30 text-amber-light'
                            : 'bg-emerald/10 border-emerald/30 text-emerald'
                        )}>
                        <span className="font-mono">T{s.tier}</span>{s.name}
                      </span>
                    ))}
                    {(d.related_incidents_expanded?.length ?? 0) > 0 && (
                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-rose/5 border border-rose/20 text-rose">
                        <Siren size={9} /> {d.related_incidents_expanded?.length} incident{(d.related_incidents_expanded?.length ?? 0) !== 1 ? 's' : ''}
                      </span>
                    )}
                    {d.tags.slice(0, 4).map(t => (
                      <span key={t} className="px-1 py-0.5 rounded text-[9px] bg-surface-raised text-text-secondary">{t}</span>
                    ))}
                  </div>
                </div>
                <ChevronRight size={14} className="text-text-muted shrink-0 mt-1" />
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-[11px] text-text-muted">No decisions match these filters.</div>
          )}
        </div>
      )}

      {selected && (
        <DecisionDetail
          decision={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setShowForm(true); setSelected(null) }}
          onDelete={() => remove(selected.id)}
        />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-primary">
                {editing ? `Edit ADR — ${editing.title}` : 'New ADR'}
              </h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null) }} title="Close" className="text-text-muted hover:text-text-primary">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <DecisionForm
                initial={editing || undefined}
                serviceOptions={services}
                incidentOptions={incidents}
                onSave={save}
                onCancel={() => { setShowForm(false); setEditing(null) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
